/**
 * Utility functions for managing OTP cooldown state reset on successful authentication
 */

/**
 * Clears all OTP cooldown data from localStorage
 * This should be called after successful login to reset any pending cooldowns
 */
export const resetAllOtpCooldowns = () => {
  try {
    // Get all localStorage keys
    const keys = Object.keys(localStorage);

    // Filter keys that match OTP cooldown pattern
    const otpCooldownKeys = keys.filter(key => key.startsWith('otp_cooldown_'));

    // Remove all OTP cooldown entries
    otpCooldownKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log(`Reset ${otpCooldownKeys.length} OTP cooldown entries`);
  } catch (error) {
    console.warn('Failed to reset OTP cooldown data:', error);
  }
};

/**
 * Clears OTP cooldown data for a specific flow type
 * @param {string} flowKey - The flow key (e.g., 'register', 'requestOtp', 'forgetPassword')
 */
export const resetOtpCooldownForFlow = (flowKey) => {
  try {
    if (!flowKey) return;

    const keys = Object.keys(localStorage);
    const flowCooldownKeys = keys.filter(key => key.startsWith(`otp_cooldown_${flowKey}`));

    flowCooldownKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn(`Failed to reset OTP cooldown data for flow ${flowKey}:`, error);
  }
};

/**
 * Clears OTP cooldown data for a specific phone number across all flows
 * @param {string} phoneNumber - The phone number to clear cooldowns for
 */
export const resetOtpCooldownForPhone = (phoneNumber) => {
  try {
    if (!phoneNumber) return;

    // Use the same normalization logic as the hook
    const normalizedPhone = normalizePhone(phoneNumber);
    if (!normalizedPhone) return;

    const keys = Object.keys(localStorage);
    const phoneCooldownKeys = keys.filter(key =>
      key.startsWith('otp_cooldown_') && key.endsWith(`_${normalizedPhone}`)
    );

    phoneCooldownKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log(`Reset ${phoneCooldownKeys.length} OTP cooldown entries for phone: ${normalizedPhone}`);
  } catch (error) {
    console.warn(`Failed to reset OTP cooldown data for phone ${phoneNumber}:`, error);
  }
};

// Phone normalization function (copied from useOtpResendCooldown hook)
const phoneNormalizationRegex = /^(?:\+?251([79]))?(0?)([79])(\d{8})$/;

const normalizePhone = (phone) => {
  if (!phone) return '';

  // Ensure phone is a string
  const phoneStr = String(phone);

  // Remove all non-digit characters and + sign for processing
  const cleanPhone = phoneStr.replace(/[^\d+]/g, '');
  const match = cleanPhone.match(phoneNormalizationRegex);

  if (match) {
    const [, countryAreaCode, leadingZero, areaCode, number] = match;

    // If we have country code area (7 or 9 from +251[79])
    if (countryAreaCode) {
      return `251${countryAreaCode}${number}`;
    }

    // If we have leading zero (07 or 09)
    if (leadingZero === '0') {
      return `251${areaCode}${number}`;
    }

    // Single digit area code (7 or 9)
    return `251${areaCode}${number}`;
  }

  // Return original if no pattern matches (fallback)
  return phoneStr.replace(/\D/g, '');
};
